/**
 * Ярлык обязательства в интерфейсе Astra — ВЫВОДИТСЯ, а не хранится.
 *
 * Прототип держал в файле одно поле `state` с пятью значениями
 * (`reported_verified | reported | silent | upcoming | in_window`). Оно
 * смешивало три независимых вопроса канона:
 *
 * 1. **что с отчётом и проверкой** — `ObligationState` из словаря выдачи
 *    (`no_data | reported | confirmed | needs_clarification | conflict`);
 * 2. **где срез относительно окна работы** — окно ещё не открылось, идёт или
 *    закрылось;
 * 3. **что со сроком** — срока нет, он не наступил, он прошёл.
 *
 * Смешение опасно ровно одним: «выполнено» становилось свойством строки, а не
 * следствием подтверждённого отчёта, и достаточно было ошибки в генераторе,
 * чтобы работа выглядела принятой. Поэтому ярлык здесь — чистая функция от
 * трёх осей, а хранить его запрещено (спека `astra-product-surface`).
 *
 * «Расхождение» — не шестой ярлык, а отдельный признак: обязательство с
 * `conflict` или `needs_clarification` ОТЧИТАНО (ярлык `reported`), но
 * проверка с отчётом не сошлась, и интерфейс обязан сказать это рядом, а не
 * вместо.
 */

import type { Schemas } from "../../api/client";
import { defineMessages } from "../../i18n/define";
import { messageRecord } from "../lib/messageRecord";

/** Ось 1: состояние «отчёт × проверка». Словарь выдачи, не наш. */
export type ObligationState = Schemas["ObligationState"];

/**
 * Ось 2: положение среза относительно окна работы.
 *
 * Значения именно три: у обязательства окно есть всегда — оно приходит от
 * работы шаблона. Случай «окна нет» — свойство РАБОТЫ (`WindowBasis.none`), и
 * обязательств у такой работы не возникает.
 */
export type WindowPosition = "before" | "open" | "closed";

/**
 * Ось 3: срок.
 *
 * `none` — не «успеваем», а «срока нет»: у необязательного слота просрочки не
 * существует в принципе (см. `DeadlineFilter.NONE` в `serving/vocab.py`), и
 * отвечать про него «не просрочено» значило бы отвечать на незаданный вопрос.
 */
export type DeadlinePosition = "none" | "ahead" | "passed";

export interface ObligationAxes {
  state: ObligationState;
  window: WindowPosition;
  deadline: DeadlinePosition;
}

/** Пять ярлыков Astra. Шестого нет, и «выполнено» среди них ровно одно. */
export type AstraLabel =
  | "reported_verified"
  | "reported"
  | "silent"
  | "upcoming"
  | "in_window";

export interface ObligationMark {
  label: AstraLabel;
  /** Отчёт и проверка разошлись: `conflict` или `needs_clarification`. */
  discrepancy: boolean;
}

/**
 * Перечисления осей. Тип здесь ВЫВОДИТСЯ из списка (`as const satisfies`), а
 * не объявляется сверху.
 *
 * Разница не стилистическая. Объяви список как `readonly ObligationState[]` —
 * и подмножество словаря стало бы допустимым значением: шестое состояние,
 * выросшее в каноне (`api/generated.ts`), прошло бы `tsc` молча, перебор
 * «всех комбинаций» перестал бы быть полным, а `obligationMark` подписал бы
 * новое состояние как «отчёт есть, проверки нет». Страж `_ALL_STATES_COVERED`
 * ниже роняет сборку по имени ровно в этот момент — решение, как показывать
 * новое состояние, принимается человеком, а не достаётся ему по умолчанию.
 */
export const ALL_OBLIGATION_STATES = [
  "no_data",
  "reported",
  "confirmed",
  "needs_clarification",
  "conflict",
] as const satisfies readonly ObligationState[];

/** Словарь выдачи вырос — этот тип перестаёт быть `true`, и сборка падает. */
type _ALL_STATES_COVERED = Exclude<
  ObligationState,
  (typeof ALL_OBLIGATION_STATES)[number]
> extends never
  ? true
  : never;
const _allStatesCovered: _ALL_STATES_COVERED = true;
void _allStatesCovered;

export const ALL_WINDOW_POSITIONS = [
  "before",
  "open",
  "closed",
] as const satisfies readonly WindowPosition[];

type _ALL_WINDOWS_COVERED = Exclude<
  WindowPosition,
  (typeof ALL_WINDOW_POSITIONS)[number]
> extends never
  ? true
  : never;
const _allWindowsCovered: _ALL_WINDOWS_COVERED = true;
void _allWindowsCovered;

export const ALL_DEADLINE_POSITIONS = [
  "none",
  "ahead",
  "passed",
] as const satisfies readonly DeadlinePosition[];

type _ALL_DEADLINES_COVERED = Exclude<
  DeadlinePosition,
  (typeof ALL_DEADLINE_POSITIONS)[number]
> extends never
  ? true
  : never;
const _allDeadlinesCovered: _ALL_DEADLINES_COVERED = true;
void _allDeadlinesCovered;

/** Подписи ярлыков. Интерфейс по-русски, и слова тоже часть контракта. */
const m = defineMessages({
  reportedVerified: {
    id: "data.obligationLabel.reportedVerified",
    defaultMessage: "отчёт подтверждён проверкой",
    description: "Ярлык работы на поле: отчёт внесён и подтверждён проверяющим (строчная фраза в строке работы)",
  },
  reported: {
    id: "data.obligationLabel.reported",
    defaultMessage: "отчёт есть, проверки нет",
    description: "Ярлык работы на поле: отчёт внесён, проверяющий его не отметил (строчная фраза)",
  },
  silent: {
    id: "data.obligationLabel.silent",
    defaultMessage: "после срока без отчёта",
    description: "Ярлык работы на поле: срок прошёл, отчёта нет (строчная фраза; это про данные, а не вывод «не сделано»)",
  },
  upcoming: {
    id: "data.obligationLabel.upcoming",
    defaultMessage: "работа впереди",
    description: "Ярлык работы на поле: окно работы ещё не открылось (строчная фраза)",
  },
  inWindow: {
    id: "data.obligationLabel.inWindow",
    defaultMessage: "окно открыто, отчёта пока нет",
    description: "Ярлык работы на поле: окно идёт, срок не прошёл, отчёта пока нет (строчная фраза)",
  },
});

export const LABEL_TEXT: Record<AstraLabel, string> = messageRecord({
  reported_verified: m.reportedVerified,
  reported: m.reported,
  silent: m.silent,
  upcoming: m.upcoming,
  in_window: m.inWindow,
});

/**
 * Ярлык по трём осям.
 *
 * Правило чтения — сверху вниз, и порядок здесь содержательный:
 *
 * * **отчёт главнее окна.** Отчёт, поданный после закрытия окна, остаётся
 *   отчётом: «молчит» про хозяйство, которое отчиталось, — неправда;
 * * **«выполнено» только по подтверждению.** `reported_verified` возвращается
 *   исключительно при `confirmed`. Ни окно, ни срок повысить отчёт не могут;
 * * **до открытия окна ничего не ждут.** Даже с просроченным сроком (такого
 *   сочетания в данных не бывает, но функция обязана ответить) обязательство
 *   в неоткрытом окне — «впереди», а не «молчит»: требовать отчёт до начала
 *   работ значит требовать невозможного;
 * * **внутри окна решает срок.** Пока срок не прошёл — «окно открыто»; после
 *   — «без отчёта после срока», и закрытия окна для этого ждать не нужно.
 */
export function obligationMark(axes: ObligationAxes): ObligationMark {
  const discrepancy =
    axes.state === "conflict" || axes.state === "needs_clarification";

  if (axes.state === "confirmed") {
    return { label: "reported_verified", discrepancy: false };
  }
  if (axes.state !== "no_data") {
    // reported, needs_clarification, conflict — отчёт подан, проверка его не
    // подтвердила. Ярлык один, а разошлась ли проверка с отчётом — рядом.
    return { label: "reported", discrepancy };
  }

  if (axes.window === "before") return { label: "upcoming", discrepancy: false };
  if (axes.window === "closed") return { label: "silent", discrepancy: false };
  return {
    label: axes.deadline === "passed" ? "silent" : "in_window",
    discrepancy: false,
  };
}

/* ------------------------------------------------------------ оси из дат */

/**
 * Положение окна по датам среза.
 *
 * Отдельная функция, а не часть ярлыка: даты приходят из разных выдач, и
 * склеивать чтение контракта с правилом ярлыка значило бы прятать правило.
 * Границы включительные с обеих сторон — день начала и день конца окна
 * принадлежат окну.
 */
export function windowPosition(
  window: { from: string; to: string },
  asOf: string,
): WindowPosition {
  if (asOf < window.from) return "before";
  if (asOf > window.to) return "closed";
  return "open";
}

/** Срок по датам среза. `null` — срока нет, и вопрос о просрочке не задан. */
export function deadlinePosition(
  dueOn: string | null,
  asOf: string,
): DeadlinePosition {
  if (!dueOn) return "none";
  return asOf > dueOn ? "passed" : "ahead";
}
